import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import AttendanceStreaks from "@/components/AttendanceStreaks";
import ExportRangeDialog from "@/components/ExportRangeDialog";
import AttendanceRow from "@/components/AttendanceRow";
import { AttendanceControls, AttendanceSummary, RecentRegisters } from "@/components/AttendanceWidgets";

const today = () => new Date().toISOString().slice(0, 10);

function useRegister(section, meetingDate) {
  const [members, setMembers] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    const { data } = await api.get("/attendance", { params: { section } });
    setHistory(data);
  }, [section]);

  useEffect(() => {
    api.get("/members", { params: { section } }).then((r) => setMembers(r.data));
    loadHistory();
  }, [section, loadHistory]);

  useEffect(() => {
    api.get("/attendance/one", { params: { section, meeting_date: meetingDate } }).then(({ data }) => {
      setStatuses(Object.fromEntries((data.records || []).map((r) => [r.member_id, r.status])));
    });
  }, [section, meetingDate]);

  const setStatus = (id, v) => setStatuses((prev) => ({ ...prev, [id]: v }));
  const setAll = (v) => setStatuses(Object.fromEntries(members.map((m) => [m.id, v])));

  const save = async () => {
    const records = members.map((m) => ({ member_id: m.id, status: statuses[m.id] || "absent" }));
    await api.post("/attendance", { section, meeting_date: meetingDate, records });
    await loadHistory();
  };

  return { members, statuses, history, setStatus, setAll, save };
}

export default function Attendance() {
  const [section, setSection] = useState("scouts");
  const [meetingDate, setMeetingDate] = useState(today());
  const [savedCount, setSavedCount] = useState(0);
  const { members, statuses, history, setStatus, setAll, save } = useRegister(section, meetingDate);

  const onSave = async () => {
    try {
      await save();
      toast.success("Attendance saved");
      setSavedCount((c) => c + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const summary = useMemo(() => {
    const p = members.filter((m) => statuses[m.id] === "present").length;
    const total = members.length;
    return { present: p, total, pct: total ? Math.round((p / total) * 100) : 0 };
  }, [statuses, members]);

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Weekly Register</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Attendance</h1>
        </div>
        <ExportRangeDialog path={`/export/attendance.csv?section=${section}`} filenameBase={`attendance-${section}`} testid="export-attendance-btn" />
      </div>

      <AttendanceControls section={section} onSection={setSection} meetingDate={meetingDate} onDate={setMeetingDate} onAll={setAll} onSave={onSave} />
      <AttendanceSummary section={section} summary={summary} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {members.length === 0 && <div className="text-muted-foreground py-8 text-center col-span-full">No members in this section.</div>}
        {members.map((m) => <AttendanceRow key={m.id} member={m} status={statuses[m.id]} onChange={setStatus} />)}
      </div>

      <AttendanceStreaks section={section} refreshKey={savedCount} />
      <RecentRegisters section={section} history={history} />
    </div>
  );
}
